import { E2eTestScenario } from '../../e2e-test-scenario.enum';
import type {
  IWorkspaceCreateClearRequest,
  IWorkspaceCreateSeedResult,
} from '../workspaces/create.interface';
import type {
  IWorkspaceMetadataEditClearRequest,
  IWorkspaceMetadataEditSeedResult,
} from '../workspaces/metadata-edit.interface';
import type {
  IWorkspaceParticipantsManagementClearRequest,
  IWorkspaceParticipantsManagementSeedResult,
} from '../workspaces/participants-management.interface';
import type { IE2eSeedResult } from './contracts.interface';

interface E2eScenarioContractMap {
  [E2eTestScenario.WorkspaceCreate]: {
    clearRequest: IWorkspaceCreateClearRequest;
    seedResult: IWorkspaceCreateSeedResult;
  };
  [E2eTestScenario.WorkspaceMetadataEdit]: {
    clearRequest: IWorkspaceMetadataEditClearRequest;
    seedResult: IWorkspaceMetadataEditSeedResult;
  };
  [E2eTestScenario.WorkspaceParticipantsManagement]: {
    clearRequest: IWorkspaceParticipantsManagementClearRequest;
    seedResult: IWorkspaceParticipantsManagementSeedResult;
  };
}

type ScenarioContract<S extends E2eTestScenario> = {
  buildClearRequest: (
    seedResult: E2eScenarioContractMap[S]['seedResult'],
  ) => E2eScenarioContractMap[S]['clearRequest'];
  requiredSeedResultStringFields: readonly StringFieldKey<
    E2eScenarioContractMap[S]['clearRequest']
  >[];
};

type StringFieldKey<TValue> = Extract<keyof TValue, string>;

const E2E_SCENARIO_CONTRACTS: {
  [S in E2eTestScenario]: ScenarioContract<S>;
} = {
  [E2eTestScenario.WorkspaceCreate]: {
    buildClearRequest: (seedResult): IWorkspaceCreateClearRequest => ({
      scenario: seedResult.scenario,
    }),
    requiredSeedResultStringFields: [],
  },
  [E2eTestScenario.WorkspaceMetadataEdit]: {
    buildClearRequest: (seedResult): IWorkspaceMetadataEditClearRequest => ({
      scenario: seedResult.scenario,
      workspaceId: seedResult.workspaceId,
      workspaceName: seedResult.workspaceName,
    }),
    requiredSeedResultStringFields: ['workspaceId', 'workspaceName'],
  },
  [E2eTestScenario.WorkspaceParticipantsManagement]: {
    buildClearRequest: (
      seedResult,
    ): IWorkspaceParticipantsManagementClearRequest => ({
      scenario: seedResult.scenario,
      workspaceId: seedResult.workspaceId,
      workspaceName: seedResult.workspaceName,
    }),
    requiredSeedResultStringFields: ['workspaceId', 'workspaceName'],
  },
};

export function getE2eScenarioContract<S extends E2eTestScenario>(
  scenario: S,
): ScenarioContract<S> {
  return E2E_SCENARIO_CONTRACTS[scenario];
}

export function buildE2eClearRequest<S extends E2eTestScenario>(
  seedResult: E2eScenarioContractMap[S]['seedResult'],
): E2eScenarioContractMap[S]['clearRequest'] {
  return getE2eScenarioContract(seedResult.scenario).buildClearRequest(
    seedResult,
  );
}

export function isE2eSeedResult(value: unknown): value is IE2eSeedResult {
  if (!isScenarioRecord(value)) {
    return false;
  }

  const contract = getE2eScenarioContract(value.scenario);

  return contract.requiredSeedResultStringFields.every(
    (field) => typeof value[field] === 'string',
  );
}

function isScenarioRecord(
  value: unknown,
): value is Record<string, unknown> & { scenario: E2eTestScenario } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'scenario' in value &&
    isE2eTestScenario(value.scenario)
  );
}

function isE2eTestScenario(value: unknown): value is E2eTestScenario {
  return (
    typeof value === 'string' &&
    Object.values(E2eTestScenario).some((scenario) => scenario === value)
  );
}
